/*
 * getSecurityLatestDate and getBaseSecurityLatestDate makes sure that the main securities latest date is within the bounds of the model (on the server)
 * getLatestDate makes sure that the latest date respects both the security and base security latest date. base Portfolios and benchmarks can rollover.
 * getEarliestDate makes sure that the earliest date respects both the security and whatever base.
 *
 * At this point, any date between earliest and latest should work.
 *
 * getWorkingPeriod Makes sure that the selected period is within bounds, else return default period
 *
 * getDatesAdjusted returns true if the dates we selected are different than the dates we display
 *
 * getStartDate and getEndDate make sure that the dates of the query are within the period and the availability
 *
 * getSelectedDate makes sure that the date selected is within the bounds of the query date, if not select the last date
 */

import { createSelector } from "reselect"
import { get, chain } from "lodash"
import moment from "moment"
import { getStartDate as getStartDateFromPeriod, getNextModeledDate, getDatesAdjusted as getDatesAdjustedHelper, SAME_DAY } from "helpers/dates"
import { getProfileDate, getProfilePeriod, getLatestDate as getLatestDatePortfolio } from "modules/portfolio-root/selectors/dates"
import { getBaseType, getBaseId } from "."
import { getUnmodeledDates } from "selectors/dates"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import { getToday } from "helpers/types/dates"

const EMPTY_OBJECT = {}
const getTodayDate = () => getToday()
const getDisplayType = state => get(state, "modules.securityProfile.displayType.id", "performance")
const getSecurityLatestDate = state => get(state, "global.data.securityAvailability.data.model.security.availability.to")
const getSecurityEarliestDate = state => get(state, "global.data.securityAvailability.data.model.security.availability.from")
const getBaseSecurityLatestDate = state => get(state, "global.data.baseSecurityAvailability.data.model.security.availability.to")
const getBaseSecurityEarliestDate = state => get(state, "global.data.baseSecurityAvailability.data.model.security.availability.from")
const getPortfoliosAndBenchmarks = state => get(state, "global.data.benchmarksAndPortfolios.data")
const getIsPortfolioContext = (_, props) => get(props, "isPortfolioContext")

export const getModelEarliestDate = state => get(state, "global.data.dates.data.model.availability.securitiesStartDate")

const getPortfoliosAndBenchmarksAvailability = createSelector(
  [getPortfoliosAndBenchmarks, getModelEarliestDate],
  (portfoliosAndBenchmarks, earliestDate) => {
    if (!portfoliosAndBenchmarks) {
      return EMPTY_OBJECT
    }
    return chain([
      ...get(portfoliosAndBenchmarks, "model.benchmarks"),
      ...get(portfoliosAndBenchmarks, "portfolios"),
    ])
      .keyBy("id")
      .mapValues(availableFrom => availableFrom > earliestDate ? availableFrom : earliestDate)
      .value()
  },
)

export const getLatestDate = createSelector(
  [getBaseType, getSecurityLatestDate, getBaseSecurityLatestDate],
  (baseType, securityLatestDate, baseSecurityLatestDate) => {
    // Portfolio and benchmarks can always be rolled over
    if (baseType === "security" && baseSecurityLatestDate && baseSecurityLatestDate < securityLatestDate) {
      return baseSecurityLatestDate
    } else {
      return securityLatestDate
    }
  },
)

export const getEarliestDate = createSelector(
  [getBaseType, getBaseId, getSecurityEarliestDate, getBaseSecurityEarliestDate, getPortfoliosAndBenchmarksAvailability, getModelEarliestDate],
  (baseType, baseId, securityEarliestDate, baseSecurityEarliestDate, pnbAvailability, modelEarliestDate) => {
    if (securityEarliestDate < modelEarliestDate) {
      securityEarliestDate = modelEarliestDate
    }

    if (!baseType) {
      return securityEarliestDate
    }

    let baseEarliestDate
    switch (baseType) {
      case "security":
      case "index":
        baseEarliestDate = baseSecurityEarliestDate
        break
      case "benchmark":
      case "portfolio":
        baseEarliestDate = get(pnbAvailability, [baseId, "availableFrom"])
        break
    }

    if (baseEarliestDate > securityEarliestDate) {
      return baseEarliestDate
    } else {
      return securityEarliestDate
    }
  })

const getWorkingPeriod = createSelector(
  [getProfilePeriod, getLatestDate, getEarliestDate, getIsIntradayAnalyticsEnabled, getTodayDate],
  (period, latestDate, earliestDate, isIntradayAnalyticsEnabled, today) => {
    if (!isIntradayAnalyticsEnabled && period === SAME_DAY) {
      return "MTD"
    }

    const latestAvailableEndDate = isIntradayAnalyticsEnabled ? today : latestDate
    const isDateOutOfBounds = date => date && (date < earliestDate || date > latestAvailableEndDate)

    if (period && (isDateOutOfBounds(period.startDate) || isDateOutOfBounds(period.endDate))) {
      return "1Y"
    }

    return period
  },
)

export const getEndDate = createSelector(
  [getWorkingPeriod, getLatestDate, getLatestDatePortfolio, getIsPortfolioContext, getIsIntradayAnalyticsEnabled, getTodayDate],
  (period, latestDate, latestDatePortfolio, isPortfolioContext, isIntradayAnalyticsEnabled, today) => {
    if (period && period.endDate) {
      return period.endDate
    }

    if (isIntradayAnalyticsEnabled) {
      return today
    }

    if (isPortfolioContext && latestDatePortfolio && latestDatePortfolio < latestDate) {
      return latestDatePortfolio
    }

    return latestDate
  },
)

export const getStartDate = createSelector(
  [getWorkingPeriod, getEndDate, getLatestDate, getEarliestDate, getDisplayType, getUnmodeledDates, getIsIntradayAnalyticsEnabled],
  (period, endDate, latestDate, earliestDate, displayType, unmodeledDates, isIntradayAnalyticsEnabled) => {
    let date
    if (period && period.startDate) {
      date = period.startDate
    } else {
      const startDate = getStartDateFromPeriod(isIntradayAnalyticsEnabled ? endDate : latestDate, period)

      if (startDate < earliestDate) {
        date = earliestDate
      } else {
        date = startDate
      }
    }
    if (displayType === "performance" && date <= earliestDate) {
      return getNextModeledDate(date, latestDate, unmodeledDates)
    }
    return date
  },
)

export const getDatesAdjusted = createSelector(
  [getWorkingPeriod, getStartDate, getEndDate, getLatestDate, getIsIntradayAnalyticsEnabled, getTodayDate],
  (period, startDate, endDate, latestDate, isIntradayAnalyticsEnabled, today) => {
    const latestAvailableEndDate = isIntradayAnalyticsEnabled ? today : latestDate
    return getDatesAdjustedHelper(period, startDate, endDate, latestAvailableEndDate)
  },
)

export const getSelectedDate = createSelector(
  [getProfileDate, getStartDate, getEndDate],
  (date, startDate, endDate) => {
    if (date && date >= startDate && date <= endDate) {
      return date
    }

    return endDate
  },
)

export const getDriftDates = createSelector(
  [getEndDate, getEarliestDate],
  (endDate, earliestDate) => {
    const startDate = moment(endDate).subtract(3, "months").format("YYYY-MM-DD")
    let driftStartDate = null

    if (startDate < earliestDate) {
      driftStartDate = earliestDate
    } else {
      driftStartDate = startDate
    }

    return { startDate: driftStartDate, endDate }
  },
)

export const getIsEndDateToday = createSelector(
  [getEndDate, getTodayDate],
  (endDate, today) => endDate === today,
)

export const getIsPeriodIntraday = createSelector(
  [getStartDate, getIsEndDateToday, getTodayDate, getIsIntradayAnalyticsEnabled],
  (startDate, isEndDateToday, today, isIntradayAnalyticsEnabled) => isIntradayAnalyticsEnabled && isEndDateToday && startDate === today,
)
